import * as THREE from "three";
import { createPointMarker } from "./PointMarker";
import { updateObstacle, updatePolyLine } from "./UpdatePolyLine";
import { generateUniformWaypoints } from "./generateUniformWaypoints";

export const cleanupObstacleObjects = (viewer) => {
  if (!viewer || !viewer.scene || !viewer.scene.scene) {
    console.warn("cleanupObstacleObjects: viewer가 유효하지 않습니다.");
    return 0;
  }

  const childrenToRemove = [];
  viewer?.scene.scene.traverse((child) => {
    if (
      child.userData &&
      (child.userData.obstacleId ||
        child.userData.type === "obstacle-marker" ||
        child.userData.type === "temp-line" ||
        child.userData.type === "final-line" ||
        child.userData.type === "final-polygon" ||
        child.userData.category === "user-created")
    ) {
      childrenToRemove.push(child);
    }
  });

  childrenToRemove.forEach((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) child.material.dispose();
    if (viewer?.scene?.scene) {
      viewer.scene.scene.remove(child);
    }
  });

  return childrenToRemove.length;
};

// 씬 오브젝트 정리 함수
export const cleanupAllObjects = (viewer, selectedEquipmentId) => {
  if (!viewer || !viewer.scene || !viewer.scene.scene) {
    console.warn("cleanupAllObjects: viewer가 유효하지 않습니다.");
    return 0;
  }
  const childrenToRemove = [];

  // 씬의 모든 자식들을 스캔
  viewer?.scene.scene.traverse((child) => {
    
    if (
      child.userData ||
      child.userData.type === "point-marker" ||
      child.userData.type === "path-line" ||
      child.userData.type === "final-line" ||
      child.userData.type === "temp-line" ||
      child.userData.type === "path-ribbon" ||
      child.type === "Line" ||
      child.type === "Mesh"
    ) {
      childrenToRemove.push(child);
    }
  });

  // 찾은 오브젝트들 제거
  childrenToRemove.forEach((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) child.material.dispose();
    if (child.marker) child.marker.dispose();
    if (viewer?.scene?.scene) {
      viewer.scene.scene.remove(child);
    }
  });

  return childrenToRemove.length;
};
export const cleanupEquipmentObjects = (viewer, selectedEquipmentId) => {
  const childrenToRemove = [];

  // 씬의 모든 자식들을 스캔

  viewer?.scene.scene.traverse((child) => {
    if (
      child.userData &&
      +child.userData.equipmentId === +selectedEquipmentId && // 농기구 ID로 필터링
      (child.userData.type === "point-marker" ||
        child.userData.type === "path-line" ||
        child.userData.type === "final-line" ||
        child.userData.type === "temp-line" ||
        child.userData.type === "path-ribbon")
    ) {
      childrenToRemove.push(child);
    }
  });

  // 찾은 오브젝트들 제거
  childrenToRemove.forEach((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) child.material.dispose();
    // if (child.marker) child.marker.dispose();
    viewer.scene.scene.remove(child);
  });

  return childrenToRemove.length;
};

export const handleObstacleReset = (
  viewer,
  setAllObstaclePoints,
  setClosedObstacleArea,
  selectedEquipmentId
) => {
  cleanupObstacleObjects(viewer, selectedEquipmentId);
  setAllObstaclePoints([]);
  setClosedObstacleArea([]);
};

// 경로 리셋
export const handlePathReset = (
  viewer,
  setPathGenerateMode,
  setPathEditMode,
  resetEquipmentPath,
  selectedEquipmentId
) => {
  cleanupAllObjects(viewer, selectedEquipmentId);

  // Potree 프로파일도 제거 (elevationProfile로 생성된 프로파일 포함)
  // clearPotreeProfiles(viewer);

  resetEquipmentPath(selectedEquipmentId);

  setPathGenerateMode(false);
  setPathEditMode(false);
};

export const handleEquipmentPathReset = (
  viewer,
  setPathGenerateMode,
  setPathEditMode,
  resetEquipmentPath,
  selectedEquipmentId
) => {
  cleanupEquipmentObjects(viewer, selectedEquipmentId);

  resetEquipmentPath(selectedEquipmentId);

  setPathGenerateMode(false);
  setPathEditMode(false);
};

export const loadExistingPath = (
  pathData,
  viewer,
  setMarkers,
  setNodes,
  setEdges,
  setRibbonMeshs,
  setPolyline,
  setRibbonMesh,
  setPathGenerateMode,
  setPathEditMode,
  setAllObstaclePoints,
  setClosedObstacleArea,
  vehicleSpec,
  closedObstacleArea,
  selectedEquipmentId,
  pathGenerateMode,
  setWaypoints,
  setEquipmentWaypoints
) => {
  // 1. 기존 데이터 초기화
  handlePathReset(
    viewer,
    setMarkers,
    setNodes,
    setEdges,
    setRibbonMeshs,
    setPolyline,
    setRibbonMesh,
    setPathGenerateMode,
    setPathEditMode,
    setAllObstaclePoints,
    setClosedObstacleArea,
    selectedEquipmentId,
    pathGenerateMode
  );

  // 2. waypointDetails를 nodes 배열로 변환
  const newNodes = pathData.waypointDetails?.map(
    (waypoint) => new THREE.Vector3(waypoint.x, waypoint.y, waypoint.z)
  );

  const newMarkers = [];

  newNodes?.forEach((node, index) => {
    const marker = createPointMarker(
      node,
      0xff0000,
      0.5,
      null,
      selectedEquipmentId,
      true
    );
    viewer.scene.scene.add(marker);
    newMarkers.push(marker);
  });

  // 4. 상태 업데이트
  setNodes(newNodes);
  setMarkers(newMarkers);

  // 5. 경로 생성
  const result = updatePolyLine(
    viewer,
    null, // polyline
    newNodes,
    null, // ribbonMesh
    vehicleSpec,
    {},
    [], // edges
    newMarkers,
    [] // ribbonMeshs
  );

  const denseWaypoints = generateUniformWaypoints(newNodes, 1.0);
  setWaypoints(denseWaypoints);
  setEquipmentWaypoints(selectedEquipmentId, denseWaypoints);

  // 6. 결과 저장
  setPolyline(result.polyline);
  setRibbonMesh(result.ribbonMesh);
  setEdges([result.polyline]);
  setRibbonMeshs([result.ribbonMesh]);

  // obstacle 장애물 활성화
  if (pathData.obstacles && pathData.obstacles.length > 0) {
    // 첫번째 좌표를 마지막에 추가해 폴리곤 완성
    pathData.obstacles.forEach((obstacle) => {
      const lastSeq = obstacle.points.length;
      const firstPoint = obstacle.points[0];
      obstacle.points.push({ ...firstPoint, sequence: lastSeq });

      const obstaclePoints = obstacle.points.map(
        (p) => new THREE.Vector3(p.x, p.y, p.z)
      );
      // 마커 추가 로직
      // obstaclePoints.forEach((p) => {
      //   const marker = createPointMarker(p, 0xff0000);
      //   marker.userData.isObstacle = true; // 클리너 예외용
      //   viewer.scene.scene.add(marker);
      // });

      const renderObjects = updateObstacle(
        viewer,
        obstaclePoints,
        obstacle.areaTypeId,
        obstacle.areaType.typeColor
      );

      setClosedObstacleArea([...closedObstacleArea, obstaclePoints]);
    });
  }
};

export const cleanupAllObstacleObjects = (viewer) => {
  const toRemove = [];

  viewer.scene.scene.traverse((child) => {
    if (
      child.userData?.obstacleId ||
      child.userData?.type === "obstacle-marker" ||
      child.userData?.type === "final-line" ||
      child.userData?.type === "final-polygon"
    ) {
      toRemove.push(child);
    }
  });

  toRemove.forEach((child) => {
    viewer.scene.scene.remove(child);
    if (child.geometry) child.geometry.dispose();
    if (child.material) child.material.dispose();
  });
};

// Potree 프로파일 제거 함수 (elevationProfile로 생성된 프로파일 포함)
export const clearPotreeProfiles = (viewer) => {
  if (!viewer?.scene) {
    console.warn("clearPotreeProfiles: viewer가 유효하지 않습니다.");
    return;
  }

  const scene = viewer.scene;
  const sceneMain = scene.scene;
  const sceneProfile = scene.sceneProfiles || scene.scene_profile;

  const removeProfile = (profile) => {
    const removeAll = (arr) => {
      arr?.forEach((obj) => {
        if (sceneMain) sceneMain.remove(obj);
        if (sceneProfile) sceneProfile.remove(obj);
        if (obj.material) obj.material.dispose?.();
        if (obj.geometry) obj.geometry.dispose?.();
      });
    };

    removeAll(profile.spheres || []);
    removeAll(profile.edges || []);
    removeAll(profile.boxes || []);
    removeAll(profile.children || []);

    // 라벨 텍스처 정리
    profile.edgeLabels?.forEach((l) => l.texture?.dispose?.());
    profile.angleLabels?.forEach((l) => l.texture?.dispose?.());
    profile.coordinateLabels?.forEach((l) => l.texture?.dispose?.());

    profile.dispose?.();
  };

  const allProfiles = [
    ...(scene.profiles || []),
    ...(scene.profileMeasurements || []),
  ];

  allProfiles.forEach(removeProfile);

  scene.profiles = [];
  scene.profileMeasurements = [];
  if (scene.profileTools) scene.profileTools.profiles = [];

  viewer.profileWindowController?.setProfile(null);
  viewer.profileWindow?.close?.();

  // Area Measurement 제거
  if (scene.measurements && Array.isArray(scene.measurements)) {
    // 모든 측정 결과 제거
    scene.measurements.forEach((measurement) => {
      if (scene.removeMeasurement) {
        scene.removeMeasurement(measurement);
      }
    });
    // 배열 비우기
    scene.measurements = [];
  }
};
